import type { CanvasEntity } from '../entityTypes';
import type { CanvasSegment, EntityRectMap } from '../types';
import { getGroupData } from '../entityTypes';
import { getHandler } from '../entities/registry';
import { materializeSegmentsFromStore } from '../stores/CanvasContext';
import { globalTasksStore } from '../../stores/globalTasksStore';
import type { CanvasStore } from '../stores/canvasStore';

function findEntityEl(kind: string, id: string): HTMLElement | null {
  const attr = getHandler(kind).dataAttribute;
  return attr ? document.querySelector(`[${attr}="${id}"]`) as HTMLElement | null : null;
}

export interface BulkDragContext {
  entityStarts: Array<{ id: string; startX: number; startY: number }>;
  groupStarts: Array<{ groupId: string; startX: number; startY: number }>;
  draggedNodeIds: string[];
  draggedAlignIds: string[];
  entityMap: Map<string, CanvasEntity>;
  entityDomMap: Map<string, HTMLElement>;
  expandedGroupDom: Array<{ el: HTMLElement; startLeft: number; startTop: number }>;
  depOverlayDom: Map<string, { el: HTMLElement; startLeft: number; startTop: number; isFocus: boolean }>;
  depToggleDom: Map<string, { el: HTMLElement; startLeft: number; startTop: number }>;
  resourceChipDom: Map<string, { el: HTMLElement; startLeft: number; startTop: number }>;
  localRectMap: EntityRectMap;
  affectedSegments: Array<{ segment: CanvasSegment; pathEls: NodeListOf<SVGPathElement> }>;
  getEffectiveDims: (ent: { kind: string; width: number; height: number; id: string }) => { width: number; height: number };
  allEntities: CanvasEntity[];
}

export function buildBulkDragContext(
  store: CanvasStore,
  dragEntityIds: string[],
  dragCollapsedGroupIds: string[],
  dragExpandedGroupIds: string[],
  measureMapRef: React.RefObject<Map<string, { width: number; height: number }>>,
): BulkDragContext {
  const ctx = store.getSnapshot().context;
  const allEntities = [
    ...Object.values(ctx.nodes),
    ...Object.values(ctx.connectors),
    ...Object.values(ctx.groups),
  ];
  const entityMap = new Map(allEntities.map(ent => [ent.id, ent]));

  // Regular entities: position from entity.x/y
  const entityStarts = dragEntityIds
    .filter(id => {
      const ent = entityMap.get(id);
      return ent && getHandler(ent.kind).canDrag;
    })
    .map(id => {
      const ent = entityMap.get(id)!;
      return { id, startX: ent.x, startY: ent.y };
    });

  // Collapsed groups: position from data.collapsedX/Y
  const groupStarts = dragCollapsedGroupIds
    .filter(id => entityMap.has(id))
    .map(id => {
      const ent = entityMap.get(id)!;
      const gd = getGroupData(ent);
      return { groupId: id, startX: gd.collapsedX ?? ent.x, startY: gd.collapsedY ?? ent.y };
    });

  // Collect entity IDs with own rect position for drop-into-group
  const draggedNodeIds = dragEntityIds.filter(id => {
    const ent = entityMap.get(id);
    return ent && getHandler(ent.kind).hasOwnRect;
  });

  // Resolve effective dimensions (auto-measured entities have width/height=0 on entity, real size in measureMapRef)
  const getEffectiveDims = (ent: { kind: string; width: number; height: number; id: string }) => {
    const handler = getHandler(ent.kind);
    if (handler.autoMeasure) {
      const measured = measureMapRef.current.get(ent.id);
      return { width: measured?.width ?? 0, height: measured?.height ?? 0 };
    }
    return { width: ent.width, height: ent.height };
  };

  // Collect all entities with meaningful dimensions for alignment snap
  const draggedAlignIds = dragEntityIds.filter(id => {
    const ent = entityMap.get(id);
    if (!ent) return false;
    const dims = getEffectiveDims(ent);
    return dims.width > 0 && dims.height > 0;
  });

  // Cache DOM references for dragged entities
  const entityDomMap = new Map<string, HTMLElement>();
  for (const sp of entityStarts) {
    const ent = entityMap.get(sp.id);
    if (ent) {
      const el = findEntityEl(ent.kind, sp.id);
      if (el) entityDomMap.set(sp.id, el);
    }
  }
  for (const sp of groupStarts) {
    const el = findEntityEl('group', sp.groupId);
    if (el) entityDomMap.set(sp.groupId, el);
  }

  // Cache DOM references + starting positions for expanded groups
  const expandedGroupDom: Array<{ el: HTMLElement; startLeft: number; startTop: number }> = [];
  for (const gId of dragExpandedGroupIds) {
    const el = findEntityEl('group', gId);
    if (el) {
      expandedGroupDom.push({
        el,
        startLeft: parseFloat(el.style.left) || 0,
        startTop: parseFloat(el.style.top) || 0,
      });
    }
  }

  // Build mutable local rect map for edge path recalculation
  const localRectMap: EntityRectMap = new Map();
  for (const ent of allEntities) {
    if (!getHandler(ent.kind).hasOwnRect) continue;
    const dims = getEffectiveDims(ent);
    localRectMap.set(ent.id, { x: ent.x, y: ent.y, width: dims.width, height: dims.height });
  }

  // Find segments that touch dragged entities and cache their SVG path elements
  const draggedIdSet = new Set(entityStarts.map(s => s.id));
  const allSegments: CanvasSegment[] = materializeSegmentsFromStore(store, globalTasksStore.getSnapshot().context.tasksById);
  const affectedSegments: Array<{
    segment: CanvasSegment;
    pathEls: NodeListOf<SVGPathElement>;
  }> = [];
  for (const seg of allSegments) {
    if (draggedIdSet.has(seg.from.entityId) || draggedIdSet.has(seg.to.entityId)) {
      const g = document.querySelector(`[data-segment-id="${seg.id}"]`);
      if (g) {
        affectedSegments.push({ segment: seg, pathEls: g.querySelectorAll('path') });
      }
    }
  }

  // Cache dep-explorer overlay DOM elements for dragged entities
  const depOverlayDom = new Map<string, { el: HTMLElement; startLeft: number; startTop: number; isFocus: boolean }>();
  const draggedIdSet2 = new Set([...entityStarts.map(s => s.id), ...groupStarts.map(s => s.groupId)]);
  for (const id of draggedIdSet2) {
    const el = document.querySelector(`[data-dep-overlay="${id}"]`) as HTMLElement | null;
    if (el) {
      depOverlayDom.set(id, {
        el,
        startLeft: parseFloat(el.style.left) || 0,
        startTop: parseFloat(el.style.top) || 0,
        isFocus: el.classList.contains('dep-explorer-focus-ring'),
      });
    }
  }

  // Cache dep-explorer toggle button DOM elements for dragged entities
  const depToggleDom = new Map<string, { el: HTMLElement; startLeft: number; startTop: number }>();
  for (const id of draggedIdSet2) {
    const el = document.querySelector(`[data-dep-explorer-toggle="${id}"]`) as HTMLElement | null;
    if (el) {
      depToggleDom.set(id, {
        el,
        startLeft: parseFloat(el.style.left) || 0,
        startTop: parseFloat(el.style.top) || 0,
      });
    }
  }

  // Cache resource chip overlay DOM elements for dragged entities
  const resourceChipDom = new Map<string, { el: HTMLElement; startLeft: number; startTop: number }>();
  for (const id of draggedIdSet2) {
    const el = document.querySelector(`[data-resource-chips="${id}"]`) as HTMLElement | null;
    if (el) {
      resourceChipDom.set(id, {
        el,
        startLeft: parseFloat(el.style.left) || 0,
        startTop: parseFloat(el.style.top) || 0,
      });
    }
  }

  return {
    entityStarts,
    groupStarts,
    draggedNodeIds,
    draggedAlignIds,
    entityMap,
    entityDomMap,
    expandedGroupDom,
    depOverlayDom,
    depToggleDom,
    resourceChipDom,
    localRectMap,
    affectedSegments,
    getEffectiveDims,
    allEntities,
  };
}
